/**
 * Systems.tsx — V4-2-4 / ADR-0027.
 *
 * Unified AI system inventory with lane switch (Buying purple /
 * Building teal). Origin chip, profile chip, oversight status chip,
 * readiness score. Supplier grouping on Buy lane.
 *
 * Folds Suppliers.tsx, SupplierDetail.tsx, AttestedCapabilities.tsx.
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
  StageTracker,
  type StageStatus,
} from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import {
  listWorkspaceSystems,
  createWorkspaceSystem,
  type WsSystemRow,
} from '../lib/api';
import { queryKeys, useInvalidate } from '../lib/query-keys';

type Lane = 'buying' | 'building';
type Origin = 'third_party_product' | 'saas_embedded' | 'bespoke_code' | 'platform_addon' | 'inhouse_other';

type SystemRow = WsSystemRow;

const ORIGIN_LABELS: Record<Origin, string> = {
  third_party_product: 'Third-party product',
  saas_embedded: 'SaaS-embedded',
  bespoke_code: 'Bespoke code',
  platform_addon: 'Platform add-on',
  inhouse_other: 'In-house',
};

const ORIGIN_OPTIONS: { value: Origin; label: string }[] = [
  { value: 'bespoke_code', label: 'Bespoke code' },
  { value: 'third_party_product', label: 'Third-party product' },
  { value: 'saas_embedded', label: 'SaaS-embedded AI' },
  { value: 'platform_addon', label: 'Platform add-on' },
  { value: 'inhouse_other', label: 'In-house (other)' },
];

export function Systems() {
  const { workspace } = useWorkspace();
  const prefix = `/w/${workspace.slug}/app`;
  const [activeLane, setActiveLane] = useState<Lane | 'all'>('all');
  const [addOpen, setAddOpen] = useState(false);
  const q = useWorkspaceQuery(
    (slug) => queryKeys.systems(slug),
    (slug) => listWorkspaceSystems(slug),
  );
  const invalidate = useInvalidate();

  const systems = useMemo(() => {
    const all = q.data?.systems ?? [];
    if (activeLane === 'all') return all;
    return all.filter((s) => s.lane === activeLane);
  }, [q.data, activeLane]);

  const counts = useMemo(() => {
    const all = q.data?.systems ?? [];
    return {
      all: all.length,
      buying: all.filter((s) => s.lane === 'buying').length,
      building: all.filter((s) => s.lane === 'building').length,
    };
  }, [q.data]);

  if (q.loading) return <Spinner label="Loading systems" />;
  if (q.error) return <Alert variant="error">{String(q.error)}</Alert>;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <PageHeader
          title="AI Systems"
          subtitle={`${counts.all} system${counts.all !== 1 ? 's' : ''}`}
        />
        {!addOpen ? (
          <Button variant="primary" onClick={() => setAddOpen(true)}>Add system</Button>
        ) : null}
      </div>

      {addOpen ? (
        <AddSystemForm
          slug={workspace.slug}
          onCreated={() => { setAddOpen(false); q.refetch(); invalidate('system:changed', workspace.slug); }}
          onCancel={() => setAddOpen(false)}
        />
      ) : null}

      <div
        role="tablist"
        aria-label="Lane filter"
        style={{
          display: 'flex',
          gap: 'var(--space-1)',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-2xs)',
        }}
      >
        <LaneTab lane="all" label={`All (${counts.all})`} active={activeLane === 'all'} onClick={() => setActiveLane('all')} />
        <LaneTab lane="buying" label={`Buying (${counts.buying})`} active={activeLane === 'buying'} onClick={() => setActiveLane('buying')} />
        <LaneTab lane="building" label={`Building (${counts.building})`} active={activeLane === 'building'} onClick={() => setActiveLane('building')} />
      </div>

      {systems.length === 0 ? (
        <EmptyState
          title={activeLane === 'all' ? 'No systems yet' : `No ${activeLane} systems`}
          description="Add systems via CSV import, IdP discovery, or manual entry."
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {systems.map((sys) => (
            <SystemCard key={sys.system_id} sys={sys} prefix={prefix} />
          ))}
        </div>
      )}
    </div>
  );
}

function AddSystemForm({
  slug,
  onCreated,
  onCancel,
}: {
  slug: string;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [lane, setLane] = useState<Lane>('building');
  const [origin, setOrigin] = useState<Origin>('bespoke_code');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createWorkspaceSystem(slug, {
        name: name.trim(),
        description: description.trim() || undefined,
        lane,
        origin,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [slug, name, description, lane, origin, onCreated]);

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
        Add AI system
      </div>
      <Field id="sys-name" label="System name">
        <input
          id="sys-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Customer support chatbot"
          style={{
            width: '100%',
            padding: 'var(--space-2) var(--space-3)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-input, var(--radius-card))',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: 'var(--font-size-2xs)',
          }}
        />
      </Field>
      <Field id="sys-desc" label="Description (optional)">
        <textarea
          id="sys-desc"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What does this system do?"
          style={{
            width: '100%',
            padding: 'var(--space-2) var(--space-3)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-input, var(--radius-card))',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: 'var(--font-size-2xs)',
            resize: 'vertical',
          }}
        />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Field id="sys-lane" label="Lane">
          <Select id="sys-lane" value={lane} onChange={(e) => setLane(e.target.value as Lane)}>
            <option value="building">Building (in-house)</option>
            <option value="buying">Buying (third-party)</option>
          </Select>
        </Field>
        <Field id="sys-origin" label="Origin">
          <Select id="sys-origin" value={origin} onChange={(e) => setOrigin(e.target.value as Origin)}>
            {ORIGIN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Field>
      </div>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <Button
          variant="primary"
          compact
          disabled={busy || !name.trim()}
          onClick={handleSubmit}
        >
          {busy ? 'Creating...' : 'Create system'}
        </Button>
        <Button variant="ghost" compact disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function LaneTab({
  lane,
  label,
  active,
  onClick,
}: {
  lane: 'all' | Lane;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  const laneColor = lane === 'buying' ? 'var(--buying)' : lane === 'building' ? 'var(--building)' : 'var(--accent)';
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: 'var(--space-2) var(--space-3)',
        border: 'none',
        borderBottom: active ? `2px solid ${laneColor}` : '2px solid transparent',
        background: 'transparent',
        color: active ? 'var(--text)' : 'var(--text-muted)',
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--font-size-2xs)',
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function SystemCard({ sys, prefix }: { sys: SystemRow; prefix: string }) {
  const laneColor = sys.lane === 'buying' ? 'var(--buying)' : 'var(--building)';
  const stages = sys.stage_snapshot_json ? JSON.parse(sys.stage_snapshot_json) as Array<{ key: string; label: string; status: StageStatus }> : null;

  return (
    <Link to={`${prefix}/systems/${sys.system_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Card>
        <div style={{
          display: 'grid',
          gap: 'var(--space-2)',
          padding: 'var(--space-3) var(--space-4)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span
              style={{
                width: 4,
                height: 24,
                borderRadius: 2,
                background: laneColor,
                flexShrink: 0,
              }}
              aria-hidden="true"
            />
            <span style={{
              flex: 1,
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-2xs)',
              fontWeight: 600,
              color: 'var(--text)',
            }}>
              {sys.name}
            </span>
            <Badge tone={sys.lane === 'buying' ? 'accent' : 'neutral'}>{sys.lane}</Badge>
            <span style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-dim)',
            }}>
              {ORIGIN_LABELS[sys.origin as Origin] ?? sys.origin}
            </span>
          </div>

          {sys.description && (
            <p style={{
              margin: 0,
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-muted)',
              lineHeight: 1.4,
            }}>
              {sys.description}
            </p>
          )}

          {stages && sys.current_stage && (
            <StageTracker
              lane={sys.lane}
              stages={stages}
              current={sys.current_stage}
              compact
            />
          )}
        </div>
      </Card>
    </Link>
  );
}
